import { useState, useEffect, useCallback, memo, useRef } from "react";
import { Typography, Stack, Box, TextField } from "@mui/material";
import FollowUpItemRadioGroup from "./FollowUpItemRadioGroup";
import FollowUpItemTextField from "./FollowUpItemTextField";
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import MicIcon from '@mui/icons-material/Mic';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';

const FollowUpItems = (props) => {
    const [followUpItems, setFollowUpItems] = useState([]);
    const [currentMicItem, setCurrentMicItem] = useState(null);

    const {
        transcript,
        listening,
        resetTranscript,
        browserSupportsSpeechRecognition
    } = useSpeechRecognition();

    const voiceTranscriptRef = useRef("");
    const latestItemValuesRef = useRef({});

    useEffect(() => {
        setFollowUpItems(props.followUpItems);

        return () => {
            setFollowUpItems([]);
        }
    }, [props.followUpItems]);
    
    useEffect(() => {
        if (!listening) {
            voiceTranscriptRef.current = "";
            resetTranscript();
        } else {
            const itemId = currentMicItem?.id;
            const latestValue = latestItemValuesRef.current[itemId];
            const stateValue = followUpItems.find((item) => item.id === itemId)?.c.answer ?? '';
            voiceTranscriptRef.current = (latestValue !== undefined ? latestValue : stateValue) + ' ';
        }
    }, [listening, props.open]);
    
    useEffect(() => {
        if (!listening) {
            return;
        }

        if (transcript) {
            props.setUpdateFollowUpItem({ id: currentMicItem?.id, question: "c", value: voiceTranscriptRef.current + replacePunctuationWords(transcript) });
        }
    }, [transcript]);

    const handleVoiceFieldChange = useCallback((item) => {
        props.setUpdateFollowUpItem(item);
        if (item.question === "c") {
            latestItemValuesRef.current[item.id] = item.value;
        }
        if (listening && item.id === currentMicItem?.id && item.question === "c") {
            voiceTranscriptRef.current = item.value;
            resetTranscript();
        } else if (listening) {
            SpeechRecognition.abortListening();
            resetTranscript();
        }
    }, [listening, currentMicItem]);

    const replacePunctuationWords = (text) => {
        const punctuationMap = {
            'comma': ',',
            'period': '.',
            'question mark': '?',
            'exclamation mark': '!',
            'colon': ':',
            'semicolon': ';',
            'dash': '-',
            'hyphen': '-',
            'new line': '\n',
            'new paragraph': '\n\n'
        };

        // Replace punctuation words
        let processedText = text.replace(/\b(comma|period|question mark|exclamation mark|colon|semicolon|dash|hyphen|new line|new paragraph)\b/gi, match => {
            return punctuationMap[match.toLowerCase()] || match;
        });

        // Remove spaces before punctuation
        processedText = processedText.replace(/\s+([,\.?!:;\-\n])/g, '$1');

        return processedText;
    };

    return (
        <>
        {followUpItems.length > 0 ? followUpItems.map((item, index) => {
            return (
                <Stack key={index} spacing={props.spacing} sx={{ width: '100%', marginBottom: props.margin}}>
                    <Box sx={{ width: '100%', display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 1}}>
                        <Typography sx={{ marginBottom: "0px !important", marginRight: props.indent }}><Typography sx={{ display: "inline", fontWeight: "bold", marginRight: props.indent }}>{"Question: "}</Typography>{item?.question}</Typography>
                        <Typography sx={{ marginBottom: "0px !important", marginRight: props.indent, whiteSpace: "nowrap" }}>{item?.mt_period}</Typography>
                    </Box>
                    <Box sx={{ width: '100%', display: "flex", alignItems: "flex-start"}}>
                        <Typography sx={{ marginBottom: "0px !important", marginRight: props.indent }}><Typography sx={{ display: "inline", fontWeight: "bold", marginRight: props.indent }}>{item?.a?.question}</Typography>{item?.a?.answer}</Typography>
                    </Box>
                    <Stack spacing={props.indent} direction="row" style={{ alignItems: "center" }} >
                        <Typography sx={{ fontWeight: "bold" }}>{item.b?.question}</Typography>
                        <FollowUpItemRadioGroup section={props.section} setSectionError={props.setSectionError} validate={props.validate} sx={{ marginBottom: "0px !important" }} options={[{ value: "yes", label: "Yes"}, { value: "no", label: "No"}]} value={item.b?.answer} onChange={props.setUpdateFollowUpItem} item={item} read={props.read}/>
                    </Stack>
                    <Stack>
                        <Typography sx={{ color: item.b?.answer !== "no" ? "gray" : "black" }}>{item.c?.question}</Typography>
                        <FollowUpItemTextField 
                            section={props.section} 
                            setSectionError={props.setSectionError} 
                            validate={props.validate} 
                            variant={"standard"} 
                            minRows={2} 
                            maxRows={30} 
                            InputProps={{ 
                                readOnly: props.read, 
                                maxLength: 4000,
                                endAdornment: item.b?.answer === "no" ? <InputAdornment position="end">
                                    <IconButton
                                        aria-label="speak"
                                        onClick={() => {
                                            if (!listening) {
                                                setCurrentMicItem(item);
                                                SpeechRecognition.startListening({ continuous: true });
                                            } else {
                                                voiceTranscriptRef.current = "";
                                                SpeechRecognition.stopListening();
                                            }
                                        }}
                                        sx={{ alignSelf: "end" }}
                                        size="small"
                                    >
                                        <MicIcon sx={{ color: listening && currentMicItem?.id === item.id ? "#3fa7d6" : "grey" }} fontSize="small" />
                                    </IconButton>
                                </InputAdornment> : null
                            }} 
                            value={item.c?.answer}
                            onChange={handleVoiceFieldChange}
                            item={item} 
                            disabled={item.b?.answer !== "no"} 
                            read={props.read}
                        />
                    </Stack>
                    {item?.d && item?.d?.answer &&
                        <Stack>           
                            <Typography>Previous comments:</Typography>
                            <TextField
                                value={item.d?.answer}
                                InputProps={{ readOnly: true }}
                                variant="standard"
                                multiline
                                fullWidth
                                
                            />
                        </Stack>
                    }
                </Stack>    
            )
        }): <Typography sx={{ fontStyle: "italic" }}>{"No follow up items"}</Typography>}
        </>
    );
}

export default memo(FollowUpItems);
